'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { useLocale } from '@/components/providers/context/locale-context';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

const currentYear = new Date().getFullYear();

interface SocialIconItem {
    alt: string;
    qrCode?: string;
    href?: string;
    // 微信类型：圆形背景+内部 SVG 绝对定位
    bgColor?: string;
    innerIcon?: string; // 内部图形路径（微信两个图标是独立内部图形）
    innerStyle?: React.CSSProperties; // 内部图形的 absolute 定位样式
    // 完整 SVG 类型（小红书/Substack 本身已是完整圆形 SVG）
    icon?: string;
}

function SocialIcon({
    alt,
    qrCode,
    href,
    bgColor,
    innerIcon,
    innerStyle,
    icon,
}: SocialIconItem) {
    const [showQr, setShowQr] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!showQr) return;
        const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setShowQr(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [showQr]);

    const iconEl = bgColor ? (
        // 微信类：圆形背景 + 内部图形绝对定位（精确还原 Figma）
        <div
            className="relative h-[24px] w-[24px] shrink-0 overflow-hidden rounded-full"
            style={{ backgroundColor: bgColor }}
        >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
                src={innerIcon ? getStaticAssetUrl(innerIcon) : undefined}
                alt=""
                className="absolute block"
                style={innerStyle}
            />
        </div>
    ) : (
        // 完整圆形 SVG（小红书/Substack）
        <div className="relative h-[24px] w-[24px] shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
                src={icon ? getStaticAssetUrl(icon) : undefined}
                alt={alt}
                className="absolute block h-full w-full"
            />
        </div>
    );

    const inner = (
        <div
            ref={containerRef}
            className="group relative cursor-pointer"
            onClick={() => {
                if (qrCode) setShowQr(!showQr);
            }}
            onMouseLeave={() => {
                if (showQr) setShowQr(false);
            }}
        >
            {iconEl}
            {qrCode && (
                <div
                    className={clsx(
                        'absolute bottom-[calc(100%+8px)] left-1/2 z-50 flex w-[0px] -translate-x-1/2 flex-col items-center rounded-full bg-white py-[10px] shadow-xl transition-opacity duration-200',
                        'tablet:group-hover:pointer-events-auto tablet:group-hover:opacity-100',
                        showQr
                            ? 'pointer-events-auto opacity-100'
                            : 'pointer-events-none opacity-0',
                    )}
                >
                    <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center overflow-hidden rounded-[8px]">
                        <Image
                            src={getStaticAssetUrl(qrCode)}
                            alt={alt + ' 二维码'}
                            width={88}
                            height={88}
                            unoptimized
                            className="h-full w-full object-cover"
                        />
                    </div>
                    {/* 中文文字竖排 */}
                    <div className="mt-[6px] flex flex-col items-center gap-[4px] text-[12px] font-normal leading-none text-[#666]">
                        {alt.split('').map((char, i) => (
                            <span key={i}>{char}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    if (href) {
        return (
            <Link href={href} target="_blank" rel="noopener noreferrer">
                {inner}
            </Link>
        );
    }
    return inner;
}

export default function GlobalFooter() {
    const { t } = useTranslation('global-footer');

    const locale = useLocale();

    const [pointX, setPointX] = useState(0);
    const [pointY, setPointY] = useState(0);
    const divRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        if (divRef.current) {
            const rect = divRef.current.getBoundingClientRect();
            setPointX(Math.round(e.clientX - rect.left));
            setPointY(Math.round(e.clientY - rect.top));
        }
    };

    useEffect(() => {
        if (divRef.current) {
            divRef.current.style.setProperty(
                '--mask',
                `radial-gradient(circle at ${pointX}px ${pointY}px, white 140px, transparent 280px)`,
            );
        }
    }, [pointX, pointY]);

    const socialIcons: SocialIconItem[] = [
        {
            // 微信订阅号：白色圆形背景，内部图形 14.4x12，left=4.8 top=6.6
            alt: '',
            bgColor: '#ffffff',
            innerIcon: getStaticAssetUrl(
                '/social/icon-wechat-subscription.svg',
            ),
            innerStyle: {
                left: '4.8px',
                top: '6.6px',
                width: '14.4px',
                height: '12px',
            },
            qrCode: getStaticAssetUrl('/subscribe/qr-dingyuehao.webp'),
        },
        {
            // 微信服务号：#e8e8e8 圆形背景，内部图形 13.2x13.2，居中
            alt: '',
            bgColor: '#e8e8e8',
            innerIcon: getStaticAssetUrl('/social/icon-wechat-service.svg'),
            innerStyle: {
                left: '50%',
                top: '50%',
                width: '13.2px',
                height: '13.2px',
                transform: 'translate(-50%, -50%)',
            },
            qrCode: getStaticAssetUrl('/subscribe/qr-gongzhonghao.webp'),
        },
        {
            // 小红书：完整圆形 SVG
            alt: '',
            icon: getStaticAssetUrl('/social/icon-substack.svg'),
            qrCode: getStaticAssetUrl('/subscribe/qr-xiaohongshu.webp'),
        },
        {
            // Substack：完整圆形 SVG，点击跳转
            alt: 'Substack',
            icon: getStaticAssetUrl('/social/icon-xiaohongshu.svg'),
            href: 'https://jojojoinai.substack.com',
        },
    ];

    return (
        <div className="flex w-full flex-col items-center justify-center bg-[#06060A]">
            <div
                id="masking_cursor_hoverable_div"
                style={{
                    cursor: `url('${getStaticAssetUrl('/torch4.webp')}'), auto`,
                }}
                className="relative flex w-full flex-col items-center justify-center"
            >
                <div className="w-full">
                    <Image
                        className="mb-[64px] mt-[80px]"
                        title="JoinAI"
                        alt="JoinAI"
                        src={getStaticAssetUrl('/bg-global-footer.webp')}
                        sizes="100vw"
                        style={{
                            width: '100%',
                            height: 'auto',
                        }}
                        width={3840}
                        height={992}
                    />
                </div>

                <div
                    id="masking_wrapper"
                    className="absolute left-0 top-0 hidden h-full w-full sm:block"
                >
                    <div className="relative h-full w-full">
                        <div
                            ref={divRef}
                            id="masking"
                            style={{
                                maskImage: 'var(--mask)',
                            }}
                            onMouseMove={handleMouseMove}
                            className="flex h-full w-full flex-col items-center justify-center"
                        >
                            <div
                                className={clsx(
                                    'j-h4 sm:j-h2 flex flex-col items-center justify-center bg-[linear-gradient(90deg,#999999,rgba(255,255,255,0.94),#999999)] bg-clip-text text-center text-transparent',
                                    {
                                        '!font-libre-bodoni': locale !== 'zh',
                                    },
                                )}
                            >
                                <div>{t('slogan.part1')}</div>
                                <div>{t('slogan.part2')}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute left-0 top-0 block h-full w-full sm:hidden">
                    <div className="flex h-full w-full items-center justify-center px-[28px]">
                        <div
                            className={clsx(
                                'j-h3 sm:j-h2 flex flex-col items-center justify-center bg-[linear-gradient(90deg,#999999,rgba(255,255,255,0.94),#999999)] bg-clip-text text-center text-transparent',
                                {
                                    '!font-libre-bodoni': locale !== 'zh',
                                },
                            )}
                        >
                            <div>{t('slogan.part1')}</div>
                            <div>{t('slogan.part2')}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex w-full max-w-[1680px] flex-col px-[20px] py-[32px] sm:px-[40px]">
                {/* 上半行：Logo + 社交图标 */}
                <div className="flex w-full items-center justify-between">
                    <Image
                        src={getStaticAssetUrl('/footer-joinai-logo.svg')}
                        alt="JoinAI Logo"
                        width={85}
                        height={22}
                    />
                    <div className="flex items-center gap-[16px]">
                        {socialIcons.map((item, index) => (
                            <SocialIcon key={index} {...item} />
                        ))}
                    </div>
                </div>

                <div className="my-[24px] h-[1px] w-full bg-[#333333]"></div>

                {/* 下半行：版权及地址信息 */}
                <div className="flex w-full flex-col-reverse items-start justify-between gap-[16px] sm:flex-row sm:items-end">
                    <div className="j-t2 flex flex-col items-start gap-[4px] text-[#A3A3A3]">
                        {locale === 'en' ? (
                            <>
                                <div>
                                    JoinAI (Hangzhou Join Intelligence
                                    Technology Co., Ltd.)
                                </div>
                                <div>
                                    Room 448, 4th Floor, Building 4, No. 66
                                    Dongxin Avenue, Binjiang District, Hangzhou,
                                    Zhejiang 310000
                                </div>
                                <div className="mt-[4px] flex flex-wrap items-center gap-[8px]">
                                    <span>
                                        China | © {currentYear} JoinAI. All
                                        rights reserved.
                                    </span>
                                    <Link
                                        href="http://beian.miit.gov.cn"
                                        target="_blank"
                                        className="transition-colors hover:text-white"
                                    >
                                        浙ICP备2021040718号-2
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>JoinAI（杭州卓印智能科技有限公司）</div>
                                <div>
                                    浙江省杭州市滨江区东信大道66号4号楼4层448室
                                    310000
                                </div>
                                <div className="mt-[4px] flex flex-wrap items-center gap-[8px]">
                                    <span>
                                        中国 | © {currentYear} JoinAI.
                                        保留所有权利。
                                    </span>
                                    <Link
                                        href="http://beian.miit.gov.cn"
                                        target="_blank"
                                        className="transition-colors hover:text-white"
                                    >
                                        浙ICP备2021040718号-2
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                    <Link
                        href={'/' + locale + '/cookie'}
                        target="_blank"
                        className="j-t2 text-white transition-colors hover:text-gray-300"
                    >
                        {t('privacy&cookie')}
                    </Link>
                </div>
            </div>
        </div>
    );
}
